/**
 * Add a comment (or reply) and optionally fan-out mention notifications.
 * Mirrors Flutter ContentReactionsRepository.addComment +
 *   NotificationsRepository.processCommentMentionsNotifications.
 */

import { CommentService } from '@/domain/services/comment/commentService';
import { notificationMentionsService } from '@/domain/services/notifications/notificationMentionsService';
import { COMMENTS_CONSTANTS } from '@/domain/constants/commentsConstants';
import type { CommentEntity } from '@/domain/entities/comment/commentEntity';
import type { NotificationInfoDto } from '@/data/dtos/info/notificationInfoDto';

export interface AddCommentParams {
  comment: CommentEntity;
  authorUserId: string;
  sendCommentNotification: boolean;
  notification: NotificationInfoDto | null;
  currentUserPopularityForReply?: number;
}

export class AddCommentUseCase {
  constructor(
    private service = CommentService.getInstance(),
    private mentionsService = notificationMentionsService,
  ) {}

  async execute(params: AddCommentParams): Promise<boolean> {
    const replyPopularity =
      params.currentUserPopularityForReply ??
      COMMENTS_CONSTANTS.DEFAULT_REPLY_POPULARITY_FOR_COMMENT;

    const success = await this.service.addComment(
      params.comment,
      params.authorUserId,
      params.sendCommentNotification ? params.notification : null,
      replyPopularity,
    );

    if (success && params.notification) {
      // Fire-and-forget: do not block UI
      this.mentionsService
        .processCommentMentions(params.comment.text, params.notification)
        .catch(() => {});
    }
    return success;
  }
}

export const addCommentUseCase = new AddCommentUseCase();
